import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";
import dfSubDays from "date-fns/subDays";

import { formatDate, getDateByTimeZone } from "@/lib/date";
import { mapDisposeApprovalRow } from "@/Models/DisposeApproval";
import { useApiStore } from "@/store/hooks";

export const CONSTANTS = {
  FETCH_ALL: "FETCH_ALL",
  SET_QUERY: "SET_QUERY",
};

export const getInitialQuery = () => {
  const zonedDateToday = getDateByTimeZone();
  const zonedDateFrom = dfSubDays(zonedDateToday, 10);

  return {
    page: 1,
    pageSize: 20,
    dateFrom: formatDate(zonedDateFrom),
    dateTo: formatDate(zonedDateToday),
    bookId: "",
    orderIdFromCorp: "",
    orderIdFromCorpSub: "",
    disposeApprovalType: "",
  };
};

const INITIAL_STATE = {
  rows: [],
  pageCount: 1,
  totalCount: 0,
  query: getInitialQuery(),
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_ALL:
      return {
        ...state,
        rows: action.rows,
        pageCount: action.pageCount,
        totalCount: action.totalCount,
      };
    case CONSTANTS.SET_QUERY:
      return {
        ...state,
        query: {
          ...state.query,
          ...action.query,
        },
      };
    default:
      return state;
  }
};

export const Context = createContext(INITIAL_STATE);

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <Context.Provider value={{ dispatch, state }}>{children}</Context.Provider>
  );
};

const buildQuery = (stateQuery) => {
  const query = {
    page: stateQuery.page,
    pageSize: stateQuery.pageSize,
  };

  if (stateQuery.dateFrom) {
    query.dateFrom = stateQuery.dateFrom;
  }

  if (stateQuery.dateTo) {
    query.dateTo = stateQuery.dateTo;
  }

  if (stateQuery.bookId) {
    query.bookId = stateQuery.bookId;
  }

  if (stateQuery.orderIdFromCorp) {
    query.orderIdFromCorp = stateQuery.orderIdFromCorp;
  }

  if (stateQuery.orderIdFromCorpSub) {
    query.orderIdFromCorpSub = stateQuery.orderIdFromCorpSub;
  }

  if (stateQuery.disposeApprovalType) {
    query.disposeApprovalType = stateQuery.disposeApprovalType;
  }

  return query;
};

export const useLostDeliveriesDisposeApprovalsStore = () => {
  const api = useApiStore();
  const { dispatch, state } = useContext(Context);

  const fetchAll = useCallback(async () => {
    const query = buildQuery(state.query);
    const { deliveries = [], totalCount = 0 } = await api.get(
      "/lost-deliveries/dispose-approvals",
      query,
    );

    dispatch({
      type: CONSTANTS.FETCH_ALL,
      rows: deliveries.map((row) => mapDisposeApprovalRow(row)),
      totalCount,
      pageCount:
        Math.floor(totalCount / state.query.pageSize) +
        (totalCount % state.query.pageSize ? 1 : 0),
    });
  }, [api.get, dispatch, state.query]);

  const setQuery = useCallback(
    (query = {}) => {
      dispatch({
        type: CONSTANTS.SET_QUERY,
        query,
      });
    },
    [dispatch],
  );

  const resetQuery = useCallback(() => {
    const query = getInitialQuery();

    dispatch({
      type: CONSTANTS.SET_QUERY,
      query,
    });

    return query;
  }, [dispatch]);

  return {
    state,
    fetchAll,
    setQuery,
    resetQuery,
  };
};
